import { AppDispatch, AppGetState, GlobalState } from '@/redux/store';
import { createSelector } from 'reselect';
import { getDeployment } from './config';
import { LOG_OUT, LOGIN_FAIL, LOGIN_SUCCESS } from './login';
import { makeGet } from '../api/helpers';

/* Action Types */
export const USER_DATA_FAIL = 'USER_DATA_FAIL';
export const USER_DATA_REQUEST = 'USER_DATA_REQUEST';
export const USER_DATA_SUCCESS = 'USER_DATA_SUCCESS';

export enum AdminGrants {
    Finance = 'finance',
}

export type UserData = {
    adminAccount: boolean;
    email: string;
    featureLogExclusion: boolean;
    houseId: number | null;
    limitedAccess: boolean;
    name: string;
    ownerLoginWithStaffPerms: boolean;
    staffPerms: any;
    userId: number;
    username: string;
};

export type UserSlice = {
    adminGrants: AdminGrants[];
    token: string;
    type: string;
    userData: UserData | {};
};

/* reducer */
export const defaultUserSlice: UserSlice = {
    adminGrants: [],
    token: null,
    type: null,
    userData: {},
};

export default function reducer(state = defaultUserSlice, action: any = {}) {
    switch (action.type) {
        case LOG_OUT:
        case LOGIN_FAIL:
        case USER_DATA_FAIL:
            return defaultUserSlice;

        case LOGIN_SUCCESS:
        case USER_DATA_SUCCESS:
            return {
                ...state,
                adminGrants: action.payload.adminGrants,
                token: action.payload.token,
                type: action.payload.type,
                userData: {
                    ...action.payload.userData,
                    userId: action.payload.id || action.payload.userData?.userId,
                },
            };

        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState): UserSlice => state.user;

export const getAuthToken = createSelector(stateSelector, (state) => state.token);
export const isAuthenticated = createSelector(getAuthToken, (token) => token !== null && token !== '');
export const getUserData = createSelector(stateSelector, (state) => state.userData);
export const getUserId = createSelector(getUserData, (userData) => userData.userId);
export const getUsername = createSelector(getUserData, (userData) => userData.username);

const getAdminGrants = createSelector([stateSelector], (state) => state.adminGrants);
export const hasFinanceAdminGrant = createSelector(
    [getAdminGrants],
    (adminGrants) => adminGrants?.some((grant) => grant === AdminGrants.Finance) ?? false
);

/* ACTION CREATORS */
const fetchUserDataFail = (errorMessage) => ({
    error: true,
    payload: errorMessage,
    type: USER_DATA_FAIL,
});

export const fetchUserData = () => async (dispatch: AppDispatch, getState: AppGetState) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);

        dispatch({
            type: USER_DATA_REQUEST,
        });

        const request = makeGet({
            apiPath: 'auth/spauser',
            authToken: authToken,
            deployment,
            path: '<ITEM-API>',
        });

        // @ts-ignore
        request.end((err, { body } = {}) => {
            if (err || (body && body.errorMessage) || ('success' in body && !body.success)) {
                dispatch(fetchUserDataFail(err || body.message));
            } else if (body && body.data && body.data.type !== 'admin') {
                dispatch(fetchUserDataFail('Unauthorized'));
            } else {
                dispatch({
                    payload: {
                        ...body.data,
                        token: authToken,
                    },
                    type: USER_DATA_SUCCESS,
                });
            }
        });
    } catch (error) {
        dispatch(fetchUserDataFail(error.message));
    }
};
